import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Icon, Text } from '@chakra-ui/react'
import React, { useContext, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { HiOutlineFolderRemove } from 'react-icons/hi'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../hooks/useToast'
import type {
  RemoveAssetsFromMediaCollectionsSuccessResponse,
  RemoveMultipleAssetsFromMediaCollectionsErrorResponse,
} from '../../interfaces'
import { MLContext } from '../../providers'
import { MediaLibraryActions } from '../../providers/MLProvider'

interface Props {
  isOpen: boolean
  onClose(): void
}

const RemoveMultipleAssetsFromMediaCollection = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const [
    { selectedFiles, selectedFolders, currentMediaCollection, refetch },
    dispatch,
  ] = useContext(MLContext)

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const removeMultipleAssetsFromMediaCollection = async (): Promise<void> => {
    const assetIds: string[] = [
      ...selectedFiles.map(file => file.fileId),
      ...selectedFolders.map(folder => folder.folderId),
    ]

    const res = (await request.post('/api/v2/media-collections/remove-assets', {
      mediaCollectionIds: [currentMediaCollection],
      assetIds,
    })) as
      | RemoveAssetsFromMediaCollectionsSuccessResponse
      | RemoveMultipleAssetsFromMediaCollectionsErrorResponse

    if (request.error) {
      if ([400, 403, 404].includes(response.status)) {
        toast({
          description: (
            res as RemoveMultipleAssetsFromMediaCollectionsErrorResponse
          ).message,
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        toast({
          description: t(
            'error_in_removing_assets_from_media_collection',
            'Error in removing assets from media collection, please try again after sometime',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      }
    } else {
      const count = assetIds.length
      toast({
        description: (
          <Trans t={t} i18nKey="assets_removed_successfully" count={count}>
            Successfully removed {{ count }} asset(s) from the media collection.
          </Trans>
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })

      window.trackEvent({
        action: 'Submit',
        category: 'Media collection',
        label: 'Assets removed - asset count',
        value: count,
      })
      dispatch({
        type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
      })
    }
  }

  return (
    <Modal size="lg" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={HiOutlineFolderRemove}
            color="appBattleshipGrey"
            backgroundColor="appPaleGrey"
            boxSize={6}
            padding="0.1em"
            borderRadius="full"
            marginRight="0.5em"
          />
          {t(
            'remove_assets_from_media_collection',
            'Remove from media collection',
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="appBattleshipGrey">
            {t(
              'remove_assets_from_media_collection_description',
              `This will remove all the selected assets from this media collection. 
              Once submitted, all users who have access to the selected assets via this 
              media collection will lose access to them.`,
            )}
          </Text>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button onClick={onClose} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="appRed"
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            onClick={async () => {
              setSubmitting(true)
              await removeMultipleAssetsFromMediaCollection()
              await new Promise(resolve => setTimeout(resolve, 1500))
              setSubmitting(false)
              onClose()
              await refetch()
            }}
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default RemoveMultipleAssetsFromMediaCollection
